import React, { useEffect, useRef, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import {
  createTheme as createThemeV5,
  ThemeProvider as ThemeProviderV5,
} from "@mui/material/styles";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import HubPageHeader from "../../components/HubPageHeader";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import api from "../../services/api";
import { has, get, isNull } from "lodash";
import {
  BarChart3,
  CalendarCheck,
  CheckCircle2,
  Clock,
  ListChecks,
  Megaphone,
  MessageCircle,
  Send,
  Users,
} from "lucide-react";
import { useDate } from "../../hooks/useDate";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";

// import { SocketContext } from "../../context/Socket/SocketContext";
import { i18n } from "../../translate/i18n";

const tLang = (key, fallback) => {
  const v = i18n.t(key);
  return !v || v === key ? fallback : v;
};

const hexToRgba = (hex, alpha = 1) => {
  if (!hex || typeof hex !== "string") return `rgba(25,118,210,${alpha})`;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return `rgba(25,118,210,${alpha})`;
  return `rgba(${r},${g},${b},${alpha})`;
};

const buildTokens = (isDark) => ({
  pageBg: isDark ? "#121212" : "#ffffff",
  cardBg: isDark ? "#11141b" : "#ffffff",
  cardBgSoft: isDark ? "#161a22" : "#fafbfd",
  border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
  borderStrong: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(15,23,42,0.09)",
  shadow: isDark
    ? "0 1px 0 rgba(255,255,255,0.02) inset, 0 8px 24px rgba(0,0,0,0.45)"
    : "0 1px 0 rgba(255,255,255,0.6) inset, 0 8px 24px rgba(15,23,42,0.06)",
  shadowHover: isDark
    ? "0 1px 0 rgba(255,255,255,0.02) inset, 0 14px 36px rgba(0,0,0,0.55)"
    : "0 1px 0 rgba(255,255,255,0.7) inset, 0 14px 36px rgba(15,23,42,0.10)",
  textPrimary: isDark ? "#e9edef" : "#0f172a",
  textSecondary: isDark ? "#8696a0" : "#64748b",
  textMuted: isDark ? "#5b6770" : "#94a3b8",
  divider: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)",
});

const StatCard = ({ title, value, icon, color, tokens, loading }) => (
  <Card
    sx={{
      position: "relative",
      height: "100%",
      minHeight: 112,
      borderRadius: 3,
      background: tokens.cardBg,
      border: tokens.border,
      boxShadow: tokens.shadow,
      transition: "transform .25s ease, box-shadow .25s ease, border-color .25s ease",
      overflow: "hidden",
      "&:hover": {
        transform: "translateY(-3px)",
        boxShadow: tokens.shadowHover,
        borderColor: hexToRgba(color, 0.45),
      },
      "&:before": {
        content: '""',
        position: "absolute",
        inset: 0,
        background: `linear-gradient(135deg, ${hexToRgba(color, 0.10)} 0%, transparent 55%)`,
        pointerEvents: "none",
      },
    }}
  >
    <CardContent sx={{ p: 2.25, position: "relative" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              color: tokens.textSecondary,
              mb: 0.5,
            }}
            noWrap
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: 28,
              fontWeight: 800,
              lineHeight: 1.1,
              color: tokens.textPrimary,
              letterSpacing: -0.4,
              wordBreak: "break-word",
            }}
          >
            {loading ? "..." : value || "-"}
          </Typography>
        </Box>
        <Avatar
          sx={{
            width: 44,
            height: 44,
            background: `linear-gradient(135deg, ${color} 0%, ${hexToRgba(color, 0.7)} 100%)`,
            color: "#fff",
            boxShadow: `0 6px 16px ${hexToRgba(color, 0.35)}`,
            flexShrink: 0,
          }}
        >
          {icon}
        </Avatar>
      </Stack>
    </CardContent>
  </Card>
);

const CampaignReport = () => {
  const history = useHistory();
  const themeV4 = useThemeV4();

  const { campaignId } = useParams();

  const [campaign, setCampaign] = useState({});
  const [validContacts, setValidContacts] = useState(0);
  const [delivered, setDelivered] = useState(0);
  const [confirmationRequested, setConfirmationRequested] = useState(0);
  const [confirmed, setConfirmed] = useState(0);
  const [percent, setPercent] = useState(0);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);
  //   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);


  const { datetimeToClient } = useDate();
  const { getPlanCompany } = usePlans();
  const isDark = (themeV4?.palette?.type || themeV4?.mode) === "dark";
  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";
  const PRIMARY_CONTRAST = themeV4?.palette?.primary?.contrastText || "#fff";
  const tokens = React.useMemo(() => buildTokens(isDark), [isDark]);
  const v5Theme = React.useMemo(
    () =>
      createThemeV5({
        palette: {
          mode: isDark ? "dark" : "light",
          primary: { main: PRIMARY_MAIN, dark: PRIMARY_DARK, contrastText: PRIMARY_CONTRAST },
          background: {
            default: isDark ? "#121212" : "#ffffff",
            paper: isDark ? "#0b0d12" : "#ffffff",
          },
          divider: isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)",
        },
      }),
    [isDark, PRIMARY_MAIN, PRIMARY_DARK, PRIMARY_CONTRAST]
  );

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs || !planConfigs.plan || !planConfigs.plan.useCampaigns) {
        toast.error(i18n.t("campaignReport.noPermission"));
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mounted.current) {
      findCampaign();
    }

    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mounted.current && has(campaign, "shipping")) {
      if (has(campaign, "contactList")) {
        const contactList = get(campaign, "contactList");
        const contacts = Array.isArray(contactList?.contacts) ? contactList.contacts : [];
        const valids = contacts.filter((c) => c.isWhatsappValid);
        setValidContacts(valids.length);
      }

      if (has(campaign, "shipping")) {
        const contacts = get(campaign, "shipping");
        const delivered = contacts.filter((c) => !isNull(c.deliveredAt));
        const confirmationRequested = contacts.filter(
          (c) => !isNull(c.confirmationRequestedAt)
        );
        const confirmed = contacts.filter(
          (c) => !isNull(c.deliveredAt) && !isNull(c.confirmationRequestedAt)
        );
        setDelivered(delivered.length);
        setConfirmationRequested(confirmationRequested.length);
        setConfirmed(confirmed.length);
        setDelivered(delivered.length);
      }
    }
  }, [campaign]);

  useEffect(() => {
    setPercent(validContacts > 0 ? (delivered / validContacts) * 100 : 0);
  }, [delivered, validContacts]);

  useEffect(() => {
    const companyId = user.companyId;
    // const socket = socketManager.GetSocket();

    const onCampaignEvent = (data) => {

      if (data.record.id === +campaignId) {
        setCampaign(data.record);

        if (data.record.status === "FINALIZADA") {
          setTimeout(() => {
            findCampaign();
          }, 5000);
        }
      }
    };
    socket.on(`company-${companyId}-campaign`, onCampaignEvent);

    return () => {
      socket.off(`company-${companyId}-campaign`, onCampaignEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const findCampaign = async () => {
    setLoading(true);
    const { data } = await api.get(`/campaigns/${campaignId}`);
    setCampaign(data);
    setLoading(false);
  };

  const formatStatus = (val) => {
    switch (val) {
      case "INATIVA":
        return i18n.t("campaignReport.inactive");
      case "PROGRAMADA":
        return i18n.t("campaignReport.scheduled");
      case "EM_ANDAMENTO":
        return i18n.t("campaignReport.process");
      case "CANCELADA":
        return i18n.t("campaignReport.cancelled");
      case "FINALIZADA":
        return i18n.t("campaignReport.finished");
      default:
        return val;
    }
  };

  const progressValue = Math.max(0, Math.min(100, Number(percent) || 0));
  const scheduledAt = campaign.scheduledAt ? datetimeToClient(campaign.scheduledAt) : "-";
  const completedAt = campaign.completedAt ? datetimeToClient(campaign.completedAt) : "-";
  const statusLabel = formatStatus(campaign.status) || "-";
  const statusColor = campaign.status === "FINALIZADA"
    ? "#2EA85A"
    : campaign.status === "CANCELADA"
      ? "#F73A2C"
      : campaign.status === "EM_ANDAMENTO"
        ? PRIMARY_MAIN
        : "#F7C82C";
  const statCards = [
    {
      title: i18n.t("campaignReport.validContacts"),
      value: validContacts,
      icon: <Users size={20} />,
      color: PRIMARY_MAIN,
    },
    {
      title: i18n.t("campaignReport.deliver"),
      value: delivered,
      icon: <CheckCircle2 size={20} />,
      color: "#2EA85A",
    },
    ...(campaign.confirmation
      ? [
          {
            title: i18n.t("campaignReport.confirmationsRequested"),
            value: confirmationRequested,
            icon: <Send size={20} />,
            color: "#F7C82C",
          },
          {
            title: i18n.t("campaignReport.confirmations"),
            value: confirmed,
            icon: <ListChecks size={20} />,
            color: "#7C3AED",
          },
        ]
      : []),
    ...(campaign.whatsappId
      ? [
          {
            title: i18n.t("campaignReport.connection"),
            value: campaign.whatsapp?.name,
            icon: <MessageCircle size={20} />,
            color: "#25D366",
          },
        ]
      : []),
    ...(campaign.contactListId
      ? [
          {
            title: i18n.t("campaignReport.contactLists"),
            value: campaign.contactList?.name,
            icon: <ListChecks size={20} />,
            color: "#8B5CF6",
          },
        ]
      : []),
    {
      title: i18n.t("campaignReport.schedule"),
      value: scheduledAt,
      icon: <Clock size={20} />,
      color: "#F59E0B",
    },
    {
      title: i18n.t("campaignReport.conclusion"),
      value: completedAt,
      icon: <CalendarCheck size={20} />,
      color: "#14B8A6",
    },
  ];

  return (
    <ThemeProviderV5 theme={v5Theme}>
      <MainContainer>
        <Box
          sx={{
            background: tokens.pageBg,
            height: "100%",
            width: "100%",
            paddingTop: "12px",
            paddingBottom: "12px",
            paddingLeft: "12px",
            paddingRight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              paddingRight: "12px",
              ...(themeV4.scrollbarStyles || {}),
            }}
          >
            <HubPageHeader
              icon={<BarChart3 size={20} strokeWidth={1.8} />}
              title={`${i18n.t("campaignReport.title")} ${campaign.name || i18n.t("campaignReport.campaign")}`}
              subtitle={tLang("campaignReport.subtitle", "Acompanhe o andamento e os resultados da campanha")}
              action={
                <Chip
                  size="small"
                  icon={<Megaphone size={14} color={statusColor} />}
                  label={statusLabel}
                  sx={{
                    background: hexToRgba(statusColor, isDark ? 0.18 : 0.10),
                    color: tokens.textPrimary,
                    border: `1px solid ${hexToRgba(statusColor, 0.25)}`,
                    fontWeight: 700,
                    fontSize: 12,
                    "& .MuiChip-icon": { ml: 0.75 },
                  }}
                />
              }
            />

            <Card
              sx={{
                mb: 2.25,
                borderRadius: 3,
                background: tokens.cardBg,
                border: tokens.border,
                boxShadow: tokens.shadow,
              }}
            >
              <CardContent sx={{ p: 2.25 }}>
                <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between" spacing={1.5} sx={{ mb: 1.75 }}>
                  <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: tokens.textSecondary, mb: 0.5 }}>
                      {i18n.t("campaignReport.status")}
                    </Typography>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: tokens.textPrimary, letterSpacing: -0.35, lineHeight: 1.15 }}>
                      {delivered} {i18n.t("campaignReport.of")} {validContacts}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 28, fontWeight: 800, color: statusColor, letterSpacing: -0.5, lineHeight: 1 }}>
                    {progressValue.toFixed(0)}%
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={progressValue}
                  sx={{
                    height: 9,
                    borderRadius: 9,
                    backgroundColor: hexToRgba(statusColor, 0.12),
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 9,
                      background: `linear-gradient(90deg, ${statusColor} 0%, ${hexToRgba(statusColor, 0.75)} 100%)`,
                    },
                  }}
                />
              </CardContent>
            </Card>

            <Grid container spacing={2.25}>
              {statCards.map((card, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                  <StatCard {...card} tokens={tokens} loading={loading} />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </MainContainer>
    </ThemeProviderV5>
  );
};

export default CampaignReport;
